import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Typography,
  Box,
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Tabs,
  Tab,
  useTheme,
} from "@mui/material";
import { db } from "../../firebase.js"; // Adjust the path
import { ref, set, get, onValue } from "firebase/database";
import Notification from "../Notification.js";
import InfoBanner from "../Navigation/InfoBanner.js";
import ApplicationCard from "../Applications/ApplicationCard.js";
import SettingsCard from "../Settings/SettingsCard.js"; // Import SettingsCard component
import {
  faSearch,
  faBolt,
  faUsersSlash,
  faCogs,
  faEyeSlash,
  faWifi,
  faBan,
  faTrash,
  faCalendarTimes,
  faBoxOpen,
  faKey,
  faCalendarCheck,
  faIndustry,
  faWindowClose,
} from "@fortawesome/free-solid-svg-icons"; // Add all the relevant icons

// Define settings icons
const settingsIcons = {
  DisableCortanaAndBingSearch: faSearch,
  DisableFastStartup: faBolt,
  DisablePeopleIconOnTaskbar: faUsersSlash,
  DisableServices: faCogs,
  DisableTrackingServices: faEyeSlash,
  DisableWiFiSenseAndLiveTiles: faWifi,
  PreventBloatwareReinstallation: faBan,
  RemoveBloatware: faTrash,
  RemoveAdditionalScheduledTasks: faCalendarTimes,
  RemoveProvisionedAppxPackages: faBoxOpen,
  RemoveRegistryKeys: faKey,
  RemoveScheduledTasks: faCalendarCheck,
  RemoveManufacturerBloat: faIndustry,
  RemoveWindows11SpecificFeatures: faWindowClose,
};

function AddCompanyForm({
  companyCodeProp = "",
  onCompanyAdded = () => {},
  open,
  onClose,
}) {
  const [companyCode, setCompanyCode] = useState(companyCodeProp || "");
  const [applications, setApplications] = useState({});
  const [applicationData, setApplicationData] = useState({});
  const [selectedSettings, setSelectedSettings] = useState({});
  const [tabIndex, setTabIndex] = useState(0); // Track active tab
  const theme = useTheme();

  // Utility function to split camelCase or PascalCase string and add spaces
  const formatSettingTitle = (str) => {
    return str.replace(/([A-Z])/g, " $1").trim();
  };

  useEffect(() => {
    const fetchApplicationData = () => {
      const appsRef = ref(db, "Applications");
      onValue(appsRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          setApplicationData(data);
        }
      });
    };

    fetchApplicationData();
  }, []);

  useEffect(() => {
    const initialApplications = {};

    if (companyCodeProp) {
      const companyRef = ref(
        db,
        `CompanyApplications/${companyCodeProp.toUpperCase()}`
      );
      get(companyRef).then((snapshot) => {
        const data = snapshot.val();
        if (data) {
          Object.keys(applicationData).forEach((appKey) => {
            initialApplications[appKey] =
              data[appKey] !== undefined ? data[appKey] : false;
          });
        } else {
          Object.keys(applicationData).forEach((appKey) => {
            initialApplications[appKey] = false;
          });
        }
        setApplications(initialApplications);
      });

      // Fetch existing company settings if editing
      const companySettingsRef = ref(db, `CompanySettings/${companyCodeProp}`);
      onValue(companySettingsRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          setSelectedSettings(data);
        }
      });
    } else {
      Object.keys(applicationData).forEach((appKey) => {
        initialApplications[appKey] =
          applicationData[appKey]?.defaultApp || false;
      });
      setApplications(initialApplications);
    }
  }, [companyCodeProp, applicationData]);

  const handleTileClick = (appKey) => {
    setApplications((prevState) => ({
      ...prevState,
      [appKey]: !prevState[appKey],
    }));
  };

  const handleSettingClick = (settingKey) => {
    setSelectedSettings((prevState) => ({
      ...prevState,
      [settingKey]: !prevState[settingKey],
    }));
  };

  const handleSubmitCompany = () => {
    const finalCompanyCode = companyCode.trim() || companyCodeProp.trim();

    if (finalCompanyCode.trim() === "") {
      Notification.error(theme, "Error", "Please enter a company code");
      return;
    }

    // Save applications to CompanyApplications
    const companyAppsRef = ref(
      db,
      `CompanyApplications/${finalCompanyCode.toUpperCase()}`
    );
    set(companyAppsRef, applications)
      .then(() => {
        Notification.success(
          theme,
          companyCodeProp ? "Company Updated" : "Company Added",
          companyCodeProp
            ? "The company applications have been updated successfully."
            : "The company applications have been added successfully."
        );
      })
      .catch((error) => {
        console.error("Error saving company applications:", error);
        Notification.error(
          theme,
          "Error",
          `There was a problem saving the company applications. Please try again. Error: ${error.message}`
        );
      });

    // Save settings to CompanySettings
    const companySettingsRef = ref(
      db,
      `CompanySettings/${finalCompanyCode.toUpperCase()}`
    );
    set(companySettingsRef, selectedSettings)
      .then(() => {
        Notification.success(
          theme,
          "Company Settings Updated",
          "The company settings have been updated successfully."
        );

        if (typeof onCompanyAdded === "function") {
          onCompanyAdded();
        }

        if (!companyCodeProp) {
          setCompanyCode("");
        }

        if (onClose) {
          onClose();
        }
      })
      .catch((error) => {
        console.error("Error saving company settings:", error);
        Notification.error(
          theme,
          "Error",
          `There was a problem saving the company settings. Please try again. Error: ${error.message}`
        );
      });
  };

  const renderApplicationCards = (appKeys) => (
    <Grid container spacing={2}>
      {appKeys.map((appKey) => (
        <Grid item xs={4} sm={3} md={2} lg={1.5} xl={1.3} key={appKey}>
          <ApplicationCard
            app={{
              ...applicationData[appKey],
              defaultApp: applications[appKey],
            }}
            onPlusClick={() => handleTileClick(appKey)}
            onMinusClick={() => handleTileClick(appKey)}
            showDeleteIcon={false}
            showPlusIcon={!applications[appKey]}
            showMinusIcon={applications[appKey]}
          />
        </Grid>
      ))}
    </Grid>
  );

  const renderSettingsCards = (settingKeys) => (
    <Grid container spacing={2}>
      {settingKeys.map((settingKey) => (
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={settingKey}>
          <SettingsCard
            settingKey={formatSettingTitle(settingKey)}
            settingIcon={settingsIcons[settingKey]}
            isSelected={selectedSettings[settingKey]}
            onClick={() => handleSettingClick(settingKey)}
          />
        </Grid>
      ))}
    </Grid>
  );

  const formContent = (
    <Box sx={{ padding: 2 }}>
      <TextField
        label="Company Code"
        value={companyCodeProp ? companyCodeProp : companyCode}
        onChange={(e) => setCompanyCode(e.target.value)}
        variant="outlined"
        fullWidth
        sx={{ mt: 2, mb: 2, backgroundColor: theme.palette.background.paper }}
        disabled={!!companyCodeProp}
      />

      {/* Tabs for Applications and Settings */}
      <Tabs
        value={tabIndex}
        onChange={(e, newIndex) => setTabIndex(newIndex)}
        indicatorColor="primary"
        textColor={theme.palette.success.text}
        sx={{ mb: 2 }}
      >
        <Tab label="Applications" />
        <Tab label="Settings" />
      </Tabs>

      {tabIndex === 0 && (
        <>
          {/* Conditionally render Install these applications */}
          {Object.keys(applications).some((appKey) => applications[appKey]) && (
            <>
              <Typography
                color={theme.palette.success.text}
                variant="h6"
                gutterBottom
              >
                Install these applications:
              </Typography>
              {renderApplicationCards(
                Object.keys(applications).filter(
                  (appKey) => applications[appKey]
                )
              )}
              <Divider sx={{ my: 2 }} />
            </>
          )}

          {/* Conditionally render Available Applications */}
          {Object.keys(applications).some((appKey) => !applications[appKey]) && (
            <>
              <Typography
                color={theme.palette.success.text}
                variant="h6"
                gutterBottom
              >
                Available Applications:
              </Typography>
              {renderApplicationCards(
                Object.keys(applications).filter(
                  (appKey) => !applications[appKey]
                )
              )}
            </>
          )}
        </>
      )}

      {tabIndex === 1 && (
        <>
          {/* Conditionally render Enable these settings */}
          {Object.keys(selectedSettings).some(
            (settingKey) => selectedSettings[settingKey]
          ) && (
            <>
              <Typography
                color={theme.palette.success.text}
                variant="h6"
                gutterBottom
              >
                Enable these settings:
              </Typography>
              {renderSettingsCards(
                Object.keys(settingsIcons).filter(
                  (settingKey) => selectedSettings[settingKey]
                )
              )}
              <Divider sx={{ my: 2 }} />
            </>
          )}

          {/* Conditionally render Available Settings */}
          {Object.keys(settingsIcons).some(
            (settingKey) => !selectedSettings[settingKey]
          ) && (
            <>
              <Typography
                color={theme.palette.success.text}
                variant="h6"
                gutterBottom
              >
                Available Settings:
              </Typography>
              {renderSettingsCards(
                Object.keys(settingsIcons).filter(
                  (settingKey) => !selectedSettings[settingKey]
                )
              )}
            </>
          )}
        </>
      )}
    </Box>
  );

  return open !== undefined ? (
    <Dialog
      sx={{ backgroundColor: theme.palette.background.default }}
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.default,
          color: "white",
        },
      }}
    >
      <DialogTitle sx={{ backgroundColor: theme.palette.primary.main }}>
        {companyCodeProp
          ? `Edit Company: ${companyCodeProp}`
          : "Add New Company"}
      </DialogTitle>
      <InfoBanner
        message={
          "Enter the Company's 3 letter code and select which applications and settings should be applied"
        }
        localStorageKey="modify-company-applications"
      />
      <DialogContent>{formContent}</DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Cancel
        </Button>
        <Button
          style={{
            color: theme.palette.info.main,
            backgroundColor: theme.palette.primary.main,
          }}
          onClick={handleSubmitCompany}
          variant="contained"
        >
          {companyCodeProp ? "Save Changes" : "Add Company"}
        </Button>
      </DialogActions>
    </Dialog>
  ) : (
    formContent
  );
}

export default AddCompanyForm;
